/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';

import template from './advertiser-list-page.component.html';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {formatAdnStat} from '../common/filters/format-adn-stat';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import * as _ from 'lodash';
import {AdvertiserApiService, AdvertiserModel} from './advertiser-api.service';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {OrderApiService} from '../order/order-api.service';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-advertiser-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvertiserListPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  @Input() readOnly = false;

  readonly itemStateName = 'app.adv.advertisers.advertiser';
  readonly teamItemStateName = 'app.adm.teams.team';
  readonly orderItemStateName = 'app.adv.orders.order';
  readonly userItemStateName = 'app.adm.users.user';

  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'team', sortable: false},
    {param: '', label: 'salesperson', sortable: false},
    {param: '', label: 'adOps', sortable: false}
  ];
  readonly list = inject(SignalAdminResourceListStateFactory).create<AdvertiserModel>(
    inject(AdvertiserApiService),
    {listId: 'fullAdvertiserList'},
    inject(DestroyRef)
  );
  readonly childHost: any = {};
  readonly selector = {outputFields: [] as any[], selFields: [] as string[]};
  advertiserFields: any[] = [];
  defaultFields: any[] = [];
  readonly perms: any;

  constructor(
    @Inject(LocalNetworkProfile) private readonly localNetworkProfile: any,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalUserPermissions) private readonly localUserPermissions: any,
    listBinding: SignalResourceListBindingService,
    orderApi: OrderApiService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.perms = this.localUserPermissions.getAllVisPermissions();
    getListChildren(this.childHost, listBinding, OBJECT_TYPE.Advertiser, orderApi, this.listLifetime);
    this.configureColumns();
  }

  get visibleColumns(): SignalAdminListColumn[] {
    const fields = this.selector.outputFields.length ? this.selector.outputFields : this.defaultFields;
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    const selected = this.selector.selFields;
    const fields = selected.length ? selected : this.defaultFields.map((field) => this.fieldId(field));
    return fields.includes(param);
  }

  toggleChildren(item: any): void {
    this.childHost.getChildren?.(item);
  }

  childList(itemId: string): any {
    return this.childHost.children?.[itemId];
  }

  changeChildPage(childList: any, page: number): void {
    if (!childList || page < 1 || page > this.totalPagesFor(childList) || page === childList.currentPage) {
      return;
    }
    childList.currentPage = page;
    childList.changePage();
  }

  totalPagesFor(childList: any): number {
    return Math.max(1, Math.ceil(childList.totalCount / childList.pageSize));
  }

  pageNumbersFor(childList: any): number[] {
    return visiblePageNumbers(childList.currentPage, this.totalPagesFor(childList));
  }

  pageRangeStartFor(childList: any): number {
    return childList.pageSize * (childList.currentPage - 1) + 1;
  }

  pageRangeEndFor(childList: any): number {
    return Math.min(childList.pageSize * childList.currentPage, childList.totalCount);
  }

  businessLinkDisabled(): boolean {
    return this.perms.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    return this.perms.MANAGE_SYSTEM !== true
      && this.perms.AX_ADVERTISER_ADMIN !== true
      && this.perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  formatStat(value: any, field?: string): string {
    return formatAdnStat(value, field, undefined, undefined, this.localNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  deliveryStat(item: any, period: string, field: string): any {
    return item?.[period]?.[field];
  }

  ngOnInit(): void {
    this.list.readOnly.set(this.readOnly);
    this.list.load();
  }

  private configureColumns(): void {
    this.advertiserFields = ['name', 'url', {param: 'OwnerTeam', label: 'team'}];
    if ((this.localNetworkInfo.getSuppInfo('elections') || []).length > 0) {
      this.advertiserFields.push('type');
    }
    this.advertiserFields = this.advertiserFields.concat(
      {param: 'externalReference', label: 'extReference'},
      'contact',
      'phone',
      'email'
    );

    if (this.localNetworkProfile.isPureMarketplacePlatform()) {
      this.advertiserFields.push(
        {param: 'bidInLastDay', label: 'status'},
        {param: '(last24HourDeliveryStats).impressionDelivery', label: 'impressionDelivery24', numeric: true},
        {param: '(last24HourDeliveryStats).renderedDelivery', label: 'renderedDelivery24', numeric: true},
        {param: '(last24HourDeliveryStats).viewableDelivery', label: 'viewableDelivery24', numeric: true},
        {param: '(last24HourDeliveryStats).clickDelivery', label: 'clickDelivery24', numeric: true},
        {param: '(last24HourDeliveryStats).spendDelivery', label: 'spendDelivery24', numeric: true},
        {param: '(last24HourDeliveryStats).viewability', label: 'viewability24', numeric: true},
        {param: '(last24HourDeliveryStats).ctr', label: 'ctr24', numeric: true},
        {param: '(last7DayDeliveryStats).impressionDelivery', label: 'impressionDelivery7', numeric: true},
        {param: '(last7DayDeliveryStats).renderedDelivery', label: 'renderedDelivery7', numeric: true},
        {param: '(last7DayDeliveryStats).viewableDelivery', label: 'viewableDelivery7', numeric: true},
        {param: '(last7DayDeliveryStats).clickDelivery', label: 'clickDelivery7', numeric: true},
        {param: '(last7DayDeliveryStats).spendDelivery', label: 'spendDelivery7', numeric: true},
        {param: '(last7DayDeliveryStats).viewability', label: 'viewability7', numeric: true},
        {param: '(last7DayDeliveryStats).ctr', label: 'ctr7', numeric: true}
      );
    }

    const skipForDefaults = [
      'type', 'phone', 'email', 'contact', 'impressionDelivery24', 'impressionDelivery7',
      'viewableDelivery24', 'clickDelivery24', 'viewability24', 'clickDelivery7',
      'viewableDelivery7', 'viewability7', 'ctr7', 'ctr24'
    ];
    this.defaultFields = _.filter(this.advertiserFields, (field: any) => {
      return skipForDefaults.indexOf(field.label || field.param || field) < 0;
    });
  }

  private normalizeColumns(fields: any[]): SignalAdminListColumn[] {
    return fields.map((field) => typeof field === 'string' ? {param: field} : {
      param: field.param ?? field.label ?? '',
      label: field.label,
      labelInstant: field.labelInstant,
      numeric: field.numeric,
      sortable: field.sortable
    });
  }

  private fieldId(field: any): string {
    return typeof field === 'string' ? field : field.param || field.label || field;
  }
}
